import { useState, useEffect } from 'react';
import { pricingAPI } from '../services/api';

/**
 * Hook to fetch and manage pricing data with monthly/yearly options
 * Handles loading, error states, and provides fallback pricing
 *
 * @param {string} currency - Currency code (e.g., 'SEK', 'EUR'). Defaults to 'SEK'
 * @returns {Object} { pricing, loading, error }
 *
 * pricing object structure:
 * {
 *   prices: {
 *     monthly: { id, amount, currency, interval, formatted: { amount, currency, display } },
 *     yearly: { id, amount, currency, interval, formatted: { amount, currency, display } }
 *   },
 *   trial: { days: 14 },
 *   savings: { amount: 69, currency: 'SEK', percentage: 15 },
 *   source: 'stripe' | 'database' | 'hardcoded_fallback' | 'fallback'
 * }
 */
export const usePricing = (currency = 'SEK') => {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchPricing = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await pricingAPI.getPricing(currency);
        if (isMounted) {
          setPricing(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch pricing:', err);
        if (isMounted) {
          setError(err.message);
          // Set fallback pricing on error with both monthly and yearly
          setPricing({
            prices: {
              monthly: {
                id: 'fallback-month',
                amount: 3900,
                currency: 'sek',
                interval: 'month',
                formatted: {
                  amount: '39',
                  currency: 'SEK',
                  display: '39 SEK/month'
                }
              },
              yearly: {
                id: 'fallback-year',
                amount: 39900,
                currency: 'sek',
                interval: 'year',
                formatted: {
                  amount: '399',
                  currency: 'SEK',
                  display: '399 SEK/year'
                }
              }
            },
            trial: {
              days: 14
            },
            savings: {
              amount: 69,
              currency: 'SEK',
              percentage: 15
            },
            source: 'fallback'
          });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchPricing();

    return () => {
      isMounted = false;
    };
  }, [currency]);

  return { pricing, loading, error };
};
